import { ViewHook } from "phoenix_live_view";

const SWIPE_MIN_PX = 50;
const PULL_PX = 100;
const WHEEL_INTENT_PX = 30;
const WHEEL_IDLE_MS = 500;
const WHEEL_LOCK_MS = 800;
const WHEEL_WINDOW = 4;
const LINE_PX = 16;

const DECK_QUERY = "(orientation: landscape) and (min-width: 54rem) and (min-height: 33rem)";

const SCROLL_KEYS = new Set([" ", "PageDown", "PageUp", "Home", "End"]);

const KEY_TARGETS: Record<string, string> = {
  ArrowRight: "next",
  PageDown: "next",
  ArrowLeft: "prev",
  PageUp: "prev",
  Home: "first",
  End: "last",
};

const isEditable = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

const isDeck = () => window.matchMedia(DECK_QUERY).matches;

const isZoomed = () => (window.visualViewport?.scale ?? 1) > 1;

const atTop = () => window.scrollY <= 0;

const atBottom = () =>
  window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 1;

const wheelPixels = (event: WheelEvent) =>
  event.deltaMode === WheelEvent.DOM_DELTA_PIXEL ? event.deltaY : event.deltaY * LINE_PX;

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

const average = (values: number[]) => sum(values) / values.length;

const isSwiping = (samples: number[], coasting: boolean) => {
  const recent = samples.slice(-WHEEL_WINDOW);
  const earlier = samples.slice(0, -WHEEL_WINDOW);

  if (sum(recent) < WHEEL_INTENT_PX) {
    return false;
  }

  return earlier.length === 0 ? !coasting : average(recent) >= average(earlier);
};

export const keyTarget = (event: KeyboardEvent, scrolling = false) => {
  if (
    event.metaKey ||
    event.ctrlKey ||
    event.altKey ||
    isEditable(event.target) ||
    (scrolling && SCROLL_KEYS.has(event.key))
  ) {
    return null;
  }

  if (event.key === " ") {
    return event.shiftKey ? "prev" : "next";
  }

  return KEY_TARGETS[event.key] ?? null;
};

class ReportDeck extends ViewHook {
  declare touchStart?: { x: number; y: number };
  declare pullOrigin?: number;
  declare pull: number;
  declare samples: number[];
  declare lastWheelAt: number;
  declare lockedUntil: number;
  declare coasting: boolean;
  declare coastTimer?: ReturnType<typeof setTimeout>;

  mounted() {
    this.pull = 0;
    this.samples = [];
    this.lastWheelAt = 0;
    this.lockedUntil = 0;
    this.coasting = false;

    this.onKeyDown = this.onKeyDown.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onTouchCancel = this.onTouchCancel.bind(this);
    this.onZoom = this.onZoom.bind(this);

    window.addEventListener("keydown", this.onKeyDown);
    this.el.addEventListener("wheel", this.onWheel, { passive: false });
    this.el.addEventListener("touchstart", this.onTouchStart, { passive: true });
    this.el.addEventListener("touchmove", this.onTouchMove, { passive: false });
    this.el.addEventListener("touchend", this.onTouchEnd);
    this.el.addEventListener("touchcancel", this.onTouchCancel);
    window.visualViewport?.addEventListener("resize", this.onZoom);

    this.onZoom();
  }

  destroyed() {
    this.setCoasting(false);
    delete document.documentElement.dataset.reportZoomed;
    window.visualViewport?.removeEventListener("resize", this.onZoom);
    window.removeEventListener("keydown", this.onKeyDown);
    this.el.removeEventListener("wheel", this.onWheel);
    this.el.removeEventListener("touchstart", this.onTouchStart);
    this.el.removeEventListener("touchmove", this.onTouchMove);
    this.el.removeEventListener("touchend", this.onTouchEnd);
    this.el.removeEventListener("touchcancel", this.onTouchCancel);
  }

  private navigate(to: string) {
    void this.pushEvent("navigate", { to });
  }

  private onKeyDown(event: KeyboardEvent) {
    const to = keyTarget(event, !isDeck());

    if (to) {
      event.preventDefault();
      this.navigate(to);
    }
  }

  private onWheel(event: WheelEvent) {
    if (event.ctrlKey || (event.target as Element).closest("[data-report-contents]")) {
      return;
    }

    const now = event.timeStamp;
    const delta = wheelPixels(event);

    if (now - this.lastWheelAt > WHEEL_IDLE_MS && now > this.lockedUntil) {
      this.samples = [];
      this.setCoasting(false);
    }

    this.lastWheelAt = now;
    this.samples = [...this.samples, Math.abs(delta)].slice(-WHEEL_WINDOW * 2);

    const swiping = isSwiping(this.samples, this.coasting);

    if (now < this.lockedUntil || (this.coasting && !swiping)) {
      event.preventDefault();
      this.setCoasting(true, now);
      return;
    }

    this.setCoasting(false);

    const to = (delta > 0 && atBottom() && "next") || (delta < 0 && atTop() && "prev");

    if (to && swiping) {
      event.preventDefault();
      this.samples = [];
      this.lockedUntil = now + WHEEL_LOCK_MS;
      this.setCoasting(true, now);
      this.navigate(to);
    }
  }

  private setCoasting(coasting: boolean, now = 0) {
    this.coasting = coasting;
    document.documentElement.toggleAttribute("data-report-coasting", coasting);
    clearTimeout(this.coastTimer);

    if (coasting) {
      const hold = Math.max(this.lockedUntil - now, 0) + WHEEL_IDLE_MS;
      this.coastTimer = setTimeout(() => this.setCoasting(false), hold);
    }
  }

  private onZoom() {
    document.documentElement.toggleAttribute("data-report-zoomed", isZoomed());
  }

  private onTouchStart(event: TouchEvent) {
    const [touch] = event.touches;
    const swipeable = event.touches.length === 1 && !isZoomed();

    this.touchStart = swipeable ? { x: touch.clientX, y: touch.clientY } : undefined;
    this.pullOrigin = atBottom() ? touch.clientY : undefined;
  }

  private onTouchMove(event: TouchEvent) {
    if (!this.touchStart || isDeck()) {
      return;
    }

    const y = event.touches[0].clientY;

    if (!atBottom()) {
      this.pullOrigin = undefined;
      this.setPull(0);
      return;
    }

    this.pullOrigin ??= y;

    const pulled = this.pullOrigin - y;

    if (pulled > 0 && event.cancelable) {
      event.preventDefault();
    }

    this.setPull(Math.min(1, Math.max(0, pulled) / PULL_PX));
  }

  private onTouchEnd(event: TouchEvent) {
    const start = this.touchStart;
    const pulled = this.pull === 1;

    this.onTouchCancel();

    if (!start) {
      return;
    }

    const [touch] = event.changedTouches;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;

    if (Math.abs(dx) >= SWIPE_MIN_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
      this.navigate(dx < 0 ? "next" : "prev");
    } else if (pulled) {
      this.navigate("next");
    }
  }

  private onTouchCancel() {
    this.touchStart = undefined;
    this.pullOrigin = undefined;
    this.setPull(0);
  }

  private setPull(pull: number) {
    if (pull === this.pull) {
      return;
    }

    this.pull = pull;
    this.el.style.setProperty("--report-pull", String(pull));
    this.el.toggleAttribute("data-report-pulled", pull === 1);
  }
}

export default ReportDeck;
